import React, { useEffect, useState } from "react";

import { Button, Image, Spinner, Table } from "react-bootstrap";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { faLinkSlash, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

import { useAuth } from "../../contexts/AuthContext";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

import GooglePicker from "../GooglePicker";
import { Link } from "react-router-dom";

const SheetsSettingsTab = ({ oath2Token, setOath2Token, settings }) => {
  const { currentUser, setCurrentUser } = useAuth();
  const [sheetsList, setSheetsList] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    const filterdSettings = settings.filter((item) => {
      return item.group === "tiktok_lising_sheets";
    });
    setSheetsList(filterdSettings);
  }, [settings]);

  const getGoogleAuthUrl = async () => {
    setConnecting(true);
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.google.oauth2,
        type: "GET",
      });
      if (data?.authUrl) {
        window.location.replace(data.authUrl);
        return;
      }
      toast.error(data?.message || "Failed to start Google authorization.");
    } catch (err) {
      toast.error(err?.message || "Failed to start Google authorization.");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Google account?")) return;
    setDisconnecting(true);
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.google.oauth2Disconnect,
        type: "POST",
      });
      if (data?.ok) {
        setOath2Token({});
        if (setCurrentUser && currentUser) {
          const nextMeta = { ...(currentUser.meta || {}) };
          delete nextMeta.oath2Token;
          setCurrentUser({ ...currentUser, meta: nextMeta });
        }
        toast.success("Google account disconnected.");
      } else {
        toast.error(data?.message || "Failed to disconnect.");
      }
    } finally {
      setDisconnecting(false);
    }
  };

  const deleteItemHandle = async (id) => {
    const confirm = window.confirm(
      `Are you sure you want to delete it? This action cannot be undone.`,
    );
    if (confirm) {
      setDeleting(true);
      const data = await Utils._apiRequest({
        path: Enums.endpoints.options.DELETE,
        pathParams: { id },
        type: "DELETE",
      });
      if (data._id) {
        const refreshData = sheetsList.filter((item) => {
          return item._id !== data._id;
        });
        setSheetsList(refreshData);
      } else {
        alert("Something was wrong!");
      }
      setDeleting(false);
    }
  };

  const connectedToken = currentUser?.meta?.oath2Token;
  const connectedUser = connectedToken?.user;

  return (
    <>
      <h6 className="border-bottom pb-2 mb-3 d-block">
        Connect Your Google Account
      </h6>
      <div className="col-lg-12 d-flex align-items-center gap-2 flex-wrap">
        <Button
          variant={connectedToken ? "success" : "secondary"}
          className="d-flex justify-content-center align-items-center"
          style={{ paddingLeft: "1.6em", paddingRight: "1.6em" }}
          onClick={getGoogleAuthUrl}
          size="lg"
          disabled={connecting}
        >
          {connecting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Connecting…
            </>
          ) : connectedToken ? (
            <>
              {connectedUser?.picture && (
                <Image
                  width={20}
                  className="me-2"
                  src={connectedUser.picture}
                  roundedCircle
                />
              )}
              {connectedUser?.name || connectedUser?.email || "Google account"}
              <small className="ms-2">
                ( <FontAwesomeIcon icon={faLinkSlash} size="sm" /> Reconnect )
              </small>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faGoogleDrive} className="me-2" />
              Connect Google Drive
            </>
          )}
        </Button>
        {connectedToken && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </Button>
        )}
      </div>

      {oath2Token.access_token ? (
        <>
          <h6 className="border-bottom pb-2 mb-0 mt-5 d-block">
            Google sheets connected files
          </h6>
          <Table striped hover className="text-nowrap">
            <thead>
              <tr>
                <th style={{ width: 30 }}>#</th>
                <th>File name</th>
                <th>Last modified</th>
                <th style={{ width: 50 }}>...</th>
              </tr>
            </thead>
            <tbody>
              {sheetsList.length ? (
                sheetsList.map((sheet, i) => {
                  return (
                    <tr
                      key={i}
                      className={`table-row${deleting ? " deleting" : ""}`}
                    >
                      <td>{i + 1}</td>
                      <td>
                        <Link
                          to={sheet.values.url}
                          className="text-dark"
                          target="_blank"
                        >
                          <Image
                            width={20}
                            className="me-2"
                            src={sheet.values.iconUrl}
                          />
                          {sheet.values?.name}
                        </Link>
                      </td>
                      <td>
                        {Utils.dateFormat(sheet.values.lastEditedUtc, false)}
                      </td>
                      <td>
                        <Button
                          className="ms-1"
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            deleteItemHandle(sheet._id);
                          }}
                          title="Delete item"
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4}>
                    <i>Nothing to show...</i>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="col-lg-12 d-flex justify-content-end">
            <GooglePicker
              oauth2Token={currentUser?.meta.oath2Token}
              setOath2Token={setOath2Token}
              sheetsList={sheetsList}
              setSheetsList={setSheetsList}
            />
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default SheetsSettingsTab;
